import { FieldGroupBlock } from '@lighthouse/block/src/blocks'
import type { FieldGroupBlockAbstract } from '@lighthouse/core'
import { getViewColumns, recTakeFilterVariable, resolveFilter, useAtomAction } from '@lighthouse/shared'
import React, { useMemo } from 'react'
import { useAsyncRetry } from 'react-use'

import { fetchFieldGroupRecordAtom } from '@/atoms/dataSource/action'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useRecord } from '@/hooks/useDataSource'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

interface FieldGroupBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: FieldGroupBlockAbstract
}

const FieldGroupBlockController: React.FunctionComponent<FieldGroupBlockControllerProps> = ({ blockData, ...rest }) => {
    const { config } = blockData
    const { viewFields, filter, sorts, pointer } = config

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { run: fetchFieldGroupRecord } = useAtomAction(fetchFieldGroupRecordAtom)

    const filterVariables = useMemo(() => filter && recTakeFilterVariable(filter?.expression), [filter])
    const parseVariableToFieldValue = useParseVariableWithSubscription(filterVariables)

    const parsedFilter = useMemo(() => {
        return resolveFilter({
            filter,
            extraParams: {
                parseVariable2Field: parseVariableToFieldValue
            }
        })
    }, [filter, parseVariableToFieldValue])

    const { value: record } = useAsyncRetry(() => {
        return fetchFieldGroupRecord({
            filter: parsedFilter,
            sorts,
            dsId: pointer
        })
    }, [pointer, sorts, parsedFilter])

    const currentRecord = useRecord(appId, envId, pointer, record?.id ?? '')
    const dataSource = useDataSource(appId, envId, pointer)

    const currentColumns = useMemo(
        () =>
            getViewColumns({
                tableProps: dataSource?.viewOptions.tableProps ?? [],
                value: viewFields,
                schema: dataSource?.schema ?? {}
            }).filter(item => item.type !== 'notes'),
        [dataSource?.schema, dataSource?.viewOptions.tableProps, viewFields]
    )

    return (
        <FieldGroupBlock
            dsId={pointer}
            blockData={blockData}
            dataSource={dataSource}
            columns={currentColumns}
            record={currentRecord}
            {...rest}
        />
    )
}

export default FieldGroupBlockController
